import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaBan,
  FaCheckCircle,
  FaEye,
  FaSpinner,
  FaSyncAlt,
  FaTimes,
  FaUsers,
} from 'react-icons/fa';
import ProductExportService, { BULK_SUBSCRIBE_JOB_STORAGE_KEY } from '../../../service/admin/productExportService';

const SUBSCRIBE_JOBS_PAGE_SIZE = 20;
/** Auto-search sellers when the query reaches this length (name, email, unique ID). */
const SUB_USER_SEARCH_MIN_CHARS = 3;

const EXPORT_JOB_STATUS_LABEL = {
  pending: 'Queued',
  processing: 'Processing',
  completed: 'Completed',
  finished: 'Finished',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const formatExportJobStatusLabel = (status) =>
  EXPORT_JOB_STATUS_LABEL[status] || String(status || 'Unknown').replace(/_/g, ' ');

const exportJobStatusBadgeModifiers = {
  pending: 'pending',
  processing: 'processing',
  completed: 'completed',
  finished: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
};

export function mergeSubscribeJobsRowFromStatus(prev, jobId, d) {
  const sid = String(jobId);
  const idx = prev.findIndex((j) => String(j.exportJobId) === sid);
  if (idx < 0) return prev;
  const row = prev[idx];
  const totalChunks = Math.max(
    1,
    Number(d.totalChunks) ||
      (row.totalChunks != null ? Number(row.totalChunks) : NaN) ||
      1,
  );
  const chunksDelivered = Number(
    d.chunksDelivered != null ? d.chunksDelivered : row.chunksDelivered ?? 0,
  );
  const downloadAvailable = Boolean(d.downloadAvailable);
  const hasLocalFile = d.status === 'completed' && downloadAvailable;
  const delivered =
    d.status === 'cancelled'
      ? false
      : d.status === 'finished' ||
        (d.status === 'completed' && !hasLocalFile && totalChunks <= 1);
  const awaitingFileDrop = Boolean(hasLocalFile);
  let totalProducts = row.totalProducts;
  if (d.totalProducts != null && `${d.totalProducts}` !== '') {
    const tn = Number(d.totalProducts);
    if (!Number.isNaN(tn)) totalProducts = tn;
  }
  const downloadFileName =
    d.fileName && String(d.fileName).trim()
      ? String(d.fileName).trim().replace(/\//g, '_').replace(/\\/g, '_').slice(0, 200)
      : row.downloadFileName;
  const next = [...prev];
  next[idx] = {
    ...row,
    status: d.status,
    processedProducts:
      d.processedProducts != null ? Number(d.processedProducts) : row.processedProducts,
    totalProducts,
    totalChunks,
    chunksDelivered,
    delivered,
    awaitingFileDrop,
    downloadFileName,
    errorMessage: d.errorMessage != null ? d.errorMessage : row.errorMessage,
    updatedAt: d.updatedAt || row.updatedAt,
  };
  return next;
}

export function formatSubscriberListSummary(users, maxShown = 3) {
  const arr = Array.isArray(users) ? users.filter(Boolean) : [];
  if (arr.length === 0) return '—';
  const labels = arr.map((u) => u.displayLabel || u.email || u.uniqueId || u._id).filter(Boolean);
  if (labels.length <= maxShown) return labels.join(', ');
  return `${labels.slice(0, maxShown).join(', ')} +${labels.length - maxShown}`;
}

/**
 * Bulk subscribe: user picker, job polling, subscription history.
 */
export function useBulkSubscribe({
  filters,
  countStats,
  exportBusy,
  setErrorMessage,
  updateDropdownDirection,
  buildExportJobSnapshotFilterLines,
  formatExportJobDateTime,
  formatExportDurationSec,
}) {
  const [jobHistoryTab, setJobHistoryTab] = useState('export_jobs');

  const [subUserSearch, setSubUserSearch] = useState('');
  const [subUserSearching, setSubUserSearching] = useState(false);
  const [subUserSuggestions, setSubUserSuggestions] = useState([]);
  const [showSubUserDropdown, setShowSubUserDropdown] = useState(false);
  const [selectedSubUsers, setSelectedSubUsers] = useState([]);
  const subUserBoxRef = useRef(null);
  const subUserSearchTimer = useRef(null);
  const [subDropdownUp, setSubDropdownUp] = useState(false);

  const [subscribeJobs, setSubscribeJobs] = useState([]);
  const [subscribeJobsHasMore, setSubscribeJobsHasMore] = useState(false);
  const [subscribeJobsLoadingMore, setSubscribeJobsLoadingMore] = useState(false);
  const subscribeJobsLengthRef = useRef(0);
  const [bulkJobFilterModalJob, setBulkJobFilterModalJob] = useState(null);

  const [bulkSubJobId, setBulkSubJobId] = useState(null);
  const [bulkSubJobStatus, setBulkSubJobStatus] = useState('idle');
  const [bulkSubProcessed, setBulkSubProcessed] = useState(0);
  const [bulkSubTotalProducts, setBulkSubTotalProducts] = useState(null);
  const [bulkSubStarting, setBulkSubStarting] = useState(false);
  const [bulkSubscribeConfirmOpen, setBulkSubscribeConfirmOpen] = useState(false);
  const bulkSubPollRef = useRef(null);
  const fetchBulkSubStatusRef = useRef(null);

  useEffect(() => {
    subscribeJobsLengthRef.current = subscribeJobs.length;
  }, [subscribeJobs.length]);

  const fetchSubscribeJobs = useCallback(async () => {
    try {
      const res = await ProductExportService.listExportJobs(SUBSCRIBE_JOBS_PAGE_SIZE, 0, 'subscribe');
      if (res?.status?.code === 200 && Array.isArray(res.data?.jobs)) {
        setSubscribeJobs(res.data.jobs);
        const hasMore =
          typeof res.data?.hasMore === 'boolean'
            ? res.data.hasMore
            : res.data.jobs.length >= SUBSCRIBE_JOBS_PAGE_SIZE;
        setSubscribeJobsHasMore(hasMore);
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  const loadMoreSubscribeJobs = useCallback(async () => {
    if (!subscribeJobsHasMore || subscribeJobsLoadingMore) return;
    const skip = subscribeJobsLengthRef.current;
    setSubscribeJobsLoadingMore(true);
    try {
      const res = await ProductExportService.listExportJobs(SUBSCRIBE_JOBS_PAGE_SIZE, skip, 'subscribe');
      if (res?.status?.code === 200 && Array.isArray(res.data?.jobs)) {
        const batch = res.data.jobs;
        const scrollToId = batch[0]?.exportJobId;
        const hasMore =
          typeof res.data?.hasMore === 'boolean'
            ? res.data.hasMore
            : batch.length >= SUBSCRIBE_JOBS_PAGE_SIZE;
        setSubscribeJobs((prev) => [...prev, ...batch]);
        setSubscribeJobsHasMore(hasMore);
        if (scrollToId) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              document.getElementById(`pe-subscribe-job-row-${scrollToId}`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            });
          });
        }
      }
    } catch (_) {
      /* ignore */
    } finally {
      setSubscribeJobsLoadingMore(false);
    }
  }, [subscribeJobsHasMore, subscribeJobsLoadingMore]);

  const runSubUserSearch = useCallback(async (queryOverride) => {
    const q = String(queryOverride ?? subUserSearch ?? '').trim();
    if (q.length < SUB_USER_SEARCH_MIN_CHARS) {
      setSubUserSuggestions([]);
      setShowSubUserDropdown(false);
      setSubUserSearching(false);
      return [];
    }
    try {
      setSubUserSearching(true);
      const res = await ProductExportService.searchSubscribeTargetUsers(q, 35);
      const users =
        res?.status?.code === 200 && Array.isArray(res.data?.users) ? res.data.users : [];
      setSubUserSuggestions(users);
      setShowSubUserDropdown(true);
      return users;
    } catch (_) {
      setSubUserSuggestions([]);
      setShowSubUserDropdown(true);
      return [];
    } finally {
      setSubUserSearching(false);
    }
  }, [subUserSearch]);

  useEffect(() => {
    if (subUserSearchTimer.current) clearTimeout(subUserSearchTimer.current);
    const q = (subUserSearch || '').trim();
    if (q.length < SUB_USER_SEARCH_MIN_CHARS) {
      setSubUserSuggestions([]);
      setShowSubUserDropdown(false);
      setSubUserSearching(false);
      return undefined;
    }
    subUserSearchTimer.current = setTimeout(() => {
      void runSubUserSearch(q);
    }, 300);
    return () => {
      if (subUserSearchTimer.current) clearTimeout(subUserSearchTimer.current);
    };
  }, [subUserSearch, runSubUserSearch]);

  useEffect(() => {
    const onClick = (e) => {
      if (subUserBoxRef.current && !subUserBoxRef.current.contains(e.target)) {
        setShowSubUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (!showSubUserDropdown) return undefined;
    const update = () => updateDropdownDirection(subUserBoxRef, setSubDropdownUp);
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showSubUserDropdown, updateDropdownDirection]);

  const toggleSubUserSelected = useCallback(
    (user) => {
      if (!user?._id) return;
      const id = String(user._id);
      setSelectedSubUsers((prev) => {
        if (prev.some((p) => String(p._id) === id)) {
          return prev.filter((p) => String(p._id) !== id);
        }
        setErrorMessage('');
        return [...prev, user];
      });
    },
    [setErrorMessage],
  );

  const handleSubUserSearchClick = useCallback(() => {
    void runSubUserSearch();
  }, [runSubUserSearch]);

  const handleSubUserSearchKeyDown = useCallback(
    async (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      const users = await runSubUserSearch();
      if (users.length === 1) {
        toggleSubUserSelected(users[0]);
        setSubUserSearch('');
      }
    },
    [runSubUserSearch, toggleSubUserSelected],
  );

  const removeSubUserChip = useCallback((userId) => {
    setSelectedSubUsers((prev) => prev.filter((p) => String(p._id) !== String(userId)));
  }, []);

  const stopBulkSubPoll = useCallback(() => {
    if (bulkSubPollRef.current) {
      clearInterval(bulkSubPollRef.current);
      bulkSubPollRef.current = null;
    }
  }, []);

  const clearBulkSubUiState = useCallback(() => {
    sessionStorage.removeItem(BULK_SUBSCRIBE_JOB_STORAGE_KEY);
    stopBulkSubPoll();
    setBulkSubJobId(null);
    setBulkSubJobStatus('idle');
    setBulkSubProcessed(0);
    setBulkSubTotalProducts(null);
    setBulkSubStarting(false);
  }, [stopBulkSubPoll]);

  const fetchBulkSubscribeJobStatus = useCallback(
    async (id) => {
      try {
        const res = await ProductExportService.getExportJobStatus(id);
        if (res?.status?.code !== 200 || !res.data) return;
        const d = res.data;
        if (String(d.jobKind || '') !== 'bulk_subscribe') return;

        setBulkSubJobStatus(d.status || 'idle');
        setBulkSubProcessed(Number(d.processedProducts) || 0);
        if (d.totalProducts != null && `${d.totalProducts}` !== '') {
          const tn = Number(d.totalProducts);
          if (!Number.isNaN(tn)) setBulkSubTotalProducts(tn);
        }
        setBulkSubStarting(false);
        setSubscribeJobs((prev) => mergeSubscribeJobsRowFromStatus(prev, id, d));

        if (['completed', 'failed', 'cancelled', 'finished'].includes(String(d.status || ''))) {
          stopBulkSubPoll();
          sessionStorage.removeItem(BULK_SUBSCRIBE_JOB_STORAGE_KEY);
          setBulkSubJobId(null);
          setBulkSubJobStatus('idle');
          setBulkSubStarting(false);
          if (String(d.status) === 'failed') {
            setErrorMessage(d.errorMessage || 'Bulk subscribe failed');
          }
          void fetchSubscribeJobs();
        }
      } catch (e) {
        const gone = e?.status === 404 || e?.data?.status?.code === 404;
        if (gone) {
          stopBulkSubPoll();
          sessionStorage.removeItem(BULK_SUBSCRIBE_JOB_STORAGE_KEY);
          setBulkSubJobId(null);
          setBulkSubJobStatus('idle');
          setBulkSubProcessed(0);
          setBulkSubTotalProducts(null);
          setBulkSubStarting(false);
          void fetchSubscribeJobs();
        }
      }
    },
    [stopBulkSubPoll, fetchSubscribeJobs, setErrorMessage],
  );

  useEffect(() => {
    fetchBulkSubStatusRef.current = fetchBulkSubscribeJobStatus;
  }, [fetchBulkSubscribeJobStatus]);

  const startBulkSubPolling = useCallback(
    (jid) => {
      stopBulkSubPoll();
      fetchBulkSubStatusRef.current?.(jid);
      bulkSubPollRef.current = setInterval(() => fetchBulkSubStatusRef.current?.(jid), 5000);
    },
    [stopBulkSubPoll],
  );

  useEffect(() => {
    fetchSubscribeJobs();
  }, [fetchSubscribeJobs]);

  useEffect(() => {
    const saved = sessionStorage.getItem(BULK_SUBSCRIBE_JOB_STORAGE_KEY);
    if (saved) {
      setBulkSubJobId(saved);
      setBulkSubStarting(true);
      startBulkSubPolling(saved);
    }
    return () => stopBulkSubPoll();
  }, [startBulkSubPolling, stopBulkSubPoll]);

  const bulkSubscribeBusy =
    bulkSubStarting || bulkSubJobStatus === 'pending' || bulkSubJobStatus === 'processing';

  const runningProgressForSubscribe = useCallback(
    (job) => {
      const base = Number(job.processedProducts) || 0;
      if (String(job.exportJobId) === String(bulkSubJobId)) {
        return Math.max(base, Number(bulkSubProcessed) || 0);
      }
      return base;
    },
    [bulkSubJobId, bulkSubProcessed],
  );

  const handleStartBulkSubscribe = useCallback(() => {
    setErrorMessage('');
    if (exportBusy) {
      setErrorMessage('Please wait for the export to finish (or cancel it) before Subscribing products to users.');
      return;
    }
    if (!filters.moq1Required) {
      setErrorMessage(
        'Please select the MOQ 1 required checkbox under Required Field Filters before bulk subscribe.',
      );
      return;
    }
    if (!selectedSubUsers.length) {
      setErrorMessage('Please search for at least one user, click Add for each person, then try again.');
      setShowSubUserDropdown(true);
      return;
    }
    setBulkSubscribeConfirmOpen(true);
  }, [exportBusy, filters.moq1Required, selectedSubUsers.length, setErrorMessage]);

  const executeBulkSubscribe = useCallback(async () => {
    try {
      setBulkSubscribeConfirmOpen(false);
      setBulkSubStarting(true);
      setBulkSubJobStatus('pending');
      setBulkSubProcessed(0);
      setBulkSubTotalProducts(null);

      const userIds = selectedSubUsers.map((u) => String(u._id));
      if (!filters.moq1Required) {
        setBulkSubStarting(false);
        setBulkSubJobStatus('idle');
        setErrorMessage(
          'Please select the MOQ 1 required checkbox under Required Field Filters before bulk subscribe.',
        );
        return;
      }
      const res = await ProductExportService.startBulkSubscribeJob(filters, userIds);
      if (res?.status?.code !== 200 || !res.data?.exportJobId) {
        setBulkSubStarting(false);
        setBulkSubJobStatus('idle');
        setErrorMessage(res?.status?.message || 'Failed to start bulk subscribe');
        return;
      }
      const id = String(res.data.exportJobId);
      if (res.data?.totalProducts != null && `${res.data.totalProducts}` !== '') {
        setBulkSubTotalProducts(Number(res.data.totalProducts));
      }
      sessionStorage.setItem(BULK_SUBSCRIBE_JOB_STORAGE_KEY, id);
      setBulkSubJobId(id);
      setJobHistoryTab('subscription_history');
      startBulkSubPolling(id);
      void fetchSubscribeJobs();
    } catch (error) {
      setBulkSubStarting(false);
      setBulkSubJobStatus('idle');
      setBulkSubTotalProducts(null);
      const conflict = error?.status === 409 || error?.data?.status?.code === 409;
      const apiMsg = error?.data?.status?.message;
      const msg = conflict
        ? apiMsg ||
          error?.message ||
          'Another job is already running. Finish or cancel it before starting bulk subscribe.'
        : apiMsg || error?.message || 'Failed to start bulk subscribe';
      setErrorMessage(msg);
    }
  }, [
    fetchSubscribeJobs,
    filters,
    selectedSubUsers,
    setErrorMessage,
    setJobHistoryTab,
    startBulkSubPolling,
  ]);

  const resetBulkSubscribeUi = useCallback(() => {
    clearBulkSubUiState();
    setSubUserSearch('');
    setSubUserSuggestions([]);
    setShowSubUserDropdown(false);
    setSubUserSearching(false);
    setSelectedSubUsers([]);
  }, [clearBulkSubUiState]);

  const bulkJobModalFilterLines = useMemo(
    () =>
      bulkJobFilterModalJob && buildExportJobSnapshotFilterLines
        ? buildExportJobSnapshotFilterLines(bulkJobFilterModalJob.filters)
        : [],
    [bulkJobFilterModalJob, buildExportJobSnapshotFilterLines],
  );

  return {
    jobHistoryTab,
    setJobHistoryTab,
    bulkSubscribeBusy,
    handleStartBulkSubscribe,
    fetchSubscribeJobs,
    clearBulkSubUiState,
    resetBulkSubscribeUi,
    bulkSubJobStatus,
    bulkSubProcessed,
    bulkSubTotalProducts,
    bulkSubscribeConfirm: {
      open: bulkSubscribeConfirmOpen,
      onClose: () => setBulkSubscribeConfirmOpen(false),
      onConfirm: executeBulkSubscribe,
      productCount: countStats?.productCount ?? null,
      sellers: selectedSubUsers,
      starting: bulkSubStarting,
    },
    picker: {
      subUserBoxRef,
      selectedSubUsers,
      setSelectedSubUsers,
      subUserSearch,
      setSubUserSearch,
      subUserSearching,
      subUserSuggestions,
      showSubUserDropdown,
      setShowSubUserDropdown,
      subDropdownUp,
      toggleSubUserSelected,
      removeSubUserChip,
      handleSubUserSearchClick,
      handleSubUserSearchKeyDown,
      setErrorMessage,
    },
    history: {
      subscribeJobs,
      subscribeJobsHasMore,
      subscribeJobsLoadingMore,
      loadMoreSubscribeJobs,
      bulkJobFilterModalJob,
      setBulkJobFilterModalJob,
      bulkJobModalFilterLines,
      runningProgressForSubscribe,
      formatExportJobDateTime,
      formatExportDurationSec,
    },
  };
}

function sellerChipLabel(user) {
  return user?.displayLabel || user?.email || user?.uniqueId || 'Seller';
}

export function BulkSubscribeConfirmModal({
  open,
  onClose,
  onConfirm,
  productCount,
  sellers,
  starting,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !starting) onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, starting]);

  if (!open) return null;

  const sellerList = Array.isArray(sellers) ? sellers.filter(Boolean) : [];
  const sellerCount = sellerList.length;
  const hasProductCount = productCount != null && Number.isFinite(Number(productCount));
  const productsPhrase = hasProductCount
    ? `about ${Number(productCount).toLocaleString()} product${Number(productCount) === 1 ? '' : 's'}`
    : 'all products that match your current filters';

  return (
    <div
      className="pe-confirm-dialog-overlay"
      role="presentation"
      onClick={() => {
        if (!starting) onClose();
      }}
    >
      <div
        className="pe-confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pe-bulk-subscribe-confirm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pe-confirm-dialog-header">
          <h3 id="pe-bulk-subscribe-confirm-title">Subscribe products to selected sellers?</h3>
          <button
            type="button"
            className="pe-export-fields-close"
            onClick={onClose}
            disabled={starting}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>
        <div className="pe-confirm-dialog-body">
          <p className="pe-confirm-dialog-lead">
            You are about to subscribe <strong>{productsPhrase}</strong> to{' '}
            <strong>
              {sellerCount} seller{sellerCount === 1 ? '' : 's'}
            </strong>
            .
          </p>
          {sellerList.length > 0 ? (
            <div className="pe-confirm-dialog-sellers">
              <span className="pe-confirm-dialog-sellers-label">Selected users</span>
              <div className="pe-job-filter-chip-wrap">
                {sellerList.map((u) => (
                  <span className="pe-job-filter-chip" key={String(u._id)}>
                    {sellerChipLabel(u)}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          <p className="pe-confirm-dialog-note">
            Only products with <strong>MOQ 1</strong> are included (MOQ 1 required filter).
            Products that are already subscribed to a user are skipped. The job runs in the background — open{' '}
            <strong>Subscription history</strong> to see progress.
          </p>
        </div>
        <div className="pe-confirm-dialog-footer">
          <button type="button" className="pe-btn-secondary" onClick={onClose} disabled={starting}>
            Cancel
          </button>
          <button type="button" className="pe-btn-primary" onClick={onConfirm} disabled={starting}>
            {starting ? (
              <>
                <FaSpinner className="pe-spin" aria-hidden /> Starting…
              </>
            ) : (
              'Start linking'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export function BulkSubscribeUserPicker({ picker }) {
  const {
    subUserBoxRef,
    selectedSubUsers,
    setSelectedSubUsers,
    subUserSearch,
    setSubUserSearch,
    subUserSearching,
    subUserSuggestions,
    showSubUserDropdown,
    setShowSubUserDropdown,
    subDropdownUp,
    toggleSubUserSelected,
    removeSubUserChip,
    handleSubUserSearchClick,
    handleSubUserSearchKeyDown,
    setErrorMessage,
  } = picker;

  return (
    <>
      <h4 className="pe-subhead">
        <FaUsers /> Bulk subscribe filtered products
      </h4>
      <div
        className={`pe-subscribe-user-row${selectedSubUsers.length ? ' pe-filter-active-box' : ''}${showSubUserDropdown ? ' pe-sub-user-dropdown-open' : ''}`}
        ref={subUserBoxRef}
      >
        <p className="pe-sub-user-hint">
          Search by name, email, or unique ID. Type at least 3 characters — results load automatically. Click Add
          for each user; selected users appear as chips below.
        </p>
        <div className="pe-mapped-user-input-row">
          <input
            type="text"
            placeholder="Search by Name, Email, or Unique ID..."
            value={subUserSearch}
            onChange={(e) => setSubUserSearch(e.target.value)}
            onFocus={() => {
              if ((subUserSearch || '').trim().length >= SUB_USER_SEARCH_MIN_CHARS) {
                setShowSubUserDropdown(true);
              }
            }}
            onKeyDown={handleSubUserSearchKeyDown}
            autoComplete="off"
          />
          <button
            type="button"
            className="pe-btn-secondary pe-search-btn"
            onClick={handleSubUserSearchClick}
            disabled={subUserSearching || (subUserSearch || '').trim().length < SUB_USER_SEARCH_MIN_CHARS}
            title={
              (subUserSearch || '').trim().length < SUB_USER_SEARCH_MIN_CHARS
                ? `Type at least ${SUB_USER_SEARCH_MIN_CHARS} characters`
                : 'Search again'
            }
          >
            {subUserSearching ? <FaSpinner className="pe-spin" aria-hidden /> : 'Search'}
          </button>
          {selectedSubUsers.length > 0 ? (
            <button
              type="button"
              className="pe-btn-secondary pe-mapped-user-clear"
              onClick={() => {
                setSelectedSubUsers([]);
                setErrorMessage('');
              }}
            >
              Clear selection
            </button>
          ) : null}
        </div>
        {showSubUserDropdown && (
          <div
            className={`pe-tag-dropdown pe-mapped-dropdown pe-sub-user-dropdown${subDropdownUp ? ' pe-tag-dropdown-up' : ''}`}
          >
            {subUserSearching ? (
              <div className="pe-tag-loading">
                <FaSpinner className="pe-spin" /> Searching…
              </div>
            ) : subUserSuggestions.length === 0 ? (
              <p className="pe-tag-empty">No users found. Try another name, email, or unique ID.</p>
            ) : (
              subUserSuggestions.map((u) => {
                const chosen = selectedSubUsers.some((p) => String(p._id) === String(u._id));
                return (
                  <div
                    key={u._id}
                    className={`pe-mapped-result-row${chosen ? ' pe-mapped-result-row-selected' : ''}`}
                  >
                    <div className="pe-mapped-result-text">
                      <span className="pe-mapped-name">{u.displayLabel || u.email || u.uniqueId}</span>
                      <span className="pe-mapped-secondary">
                        {[u.email, u.uniqueId ? `Unique ID: ${u.uniqueId}` : ''].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="pe-btn-secondary pe-mapped-select-btn"
                      onClick={() => toggleSubUserSelected(u)}
                    >
                      {chosen ? 'Remove' : 'Add'}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}
        {selectedSubUsers.length > 0 ? (
          <div className="pe-tag-chips pe-subscriber-chips">
            {selectedSubUsers.map((u) => (
              <span key={u._id} className="pe-tag-chip">
                {u.displayLabel || u.email || u.uniqueId}
                <button
                  type="button"
                  onClick={() => removeSubUserChip(u._id)}
                  className="pe-tag-chip-remove"
                  aria-label={`Remove ${u.displayLabel || u.email}`}
                >
                  <FaTimes />
                </button>
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
}

export function BulkSubscribeProgress({ bulkSubJobStatus, bulkSubProcessed, bulkSubTotalProducts }) {
  if (bulkSubJobStatus !== 'pending' && bulkSubJobStatus !== 'processing') return null;
  return (
    <div className="pe-job-status pe-job-status-bulk" role="status" aria-live="polite">
      <FaSpinner className="pe-spin pe-spin-sm pe-job-status-icon" aria-hidden />
      <div className="pe-job-status-body">
        {bulkSubJobStatus === 'pending' ? (
          <div className="pe-job-status-line pe-job-status-main">Bulk subscribe: queued — starting shortly.</div>
        ) : (
          <div className="pe-job-status-line pe-job-status-main">
            Bulk subscribe: <strong>{bulkSubProcessed.toLocaleString()}</strong>
            {bulkSubTotalProducts != null && Number.isFinite(bulkSubTotalProducts) ? (
              <>
                {' '}
                of <strong>{bulkSubTotalProducts.toLocaleString()}</strong> parent products
              </>
            ) : null}
            . Check <strong>Subscription history</strong> for live progress.
          </div>
        )}
      </div>
    </div>
  );
}

export function SubscriptionHistoryPanel({
  history,
  bulkSubscribeBusy,
  onRefresh,
  cancelActiveExportJob,
  removingJobId,
}) {
  const {
    subscribeJobs,
    subscribeJobsHasMore,
    subscribeJobsLoadingMore,
    loadMoreSubscribeJobs,
    setBulkJobFilterModalJob,
    runningProgressForSubscribe,
    formatExportJobDateTime,
    formatExportDurationSec,
  } = history;

  return (
    <div
      className="pe-export-jobs-wrap"
      role="tabpanel"
      id="pe-job-history-subscribe"
      aria-labelledby="pe-job-tab-subscribe"
    >
      <div className="pe-export-jobs-head">
        <h4 className="pe-export-jobs-title">Bulk subscribe history</h4>
        <button type="button" className="pe-btn-secondary pe-btn-tiny-inline" onClick={onRefresh}>
          <FaSyncAlt /> Refresh
        </button>
      </div>

      {subscribeJobs.length > 0 && (
        <div className="pe-export-history-scroll">
          <table className="pe-export-history-table">
            <thead>
              <tr>
                <th className="pe-export-job-sno-col" scope="col">
                  S.No
                </th>
                <th className="pe-export-job-user-head" scope="col">
                  Subscribed By
                </th>
                <th scope="col">Subscribers</th>
                <th scope="col">Status</th>
                <th scope="col">Products</th>
                <th scope="col">Started</th>
                <th scope="col">Duration</th>
                <th className="pe-export-job-actions-col" scope="col">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {subscribeJobs.map((job, index) => {
                const mod = exportJobStatusBadgeModifiers[job.status] || 'unknown';
                const subStillRunning = job.status === 'pending' || job.status === 'processing';
                const productCell = subStillRunning
                  ? runningProgressForSubscribe(job)
                  : Number(job.processedProducts || 0);
                const jobSubTotal =
                  job.totalProducts != null && Number.isFinite(Number(job.totalProducts))
                    ? Number(job.totalProducts)
                    : null;
                const canRemoveRunningJob = job.status === 'pending' || job.status === 'processing';
                const fullSubTitle = Array.isArray(job.subscriberUsers)
                  ? job.subscriberUsers
                      .map((u) => u.displayLabel || u.email || u.uniqueId || u._id)
                      .filter(Boolean)
                      .join('\n')
                  : '';
                return (
                  <tr key={job.exportJobId} id={`pe-subscribe-job-row-${job.exportJobId}`}>
                    <td className="pe-export-job-sno-col">{index + 1}</td>
                    <td
                      className="pe-export-job-user-col"
                      title={job.userEmail ? String(job.userEmail) : undefined}
                    >
                      {job.userEmail ? String(job.userEmail) : '—'}
                    </td>
                    <td className="pe-subscribe-names-col" title={fullSubTitle}>
                      {formatSubscriberListSummary(job.subscriberUsers)}
                    </td>
                    <td>
                      <span className={`pe-job-badge pe-job-badge--${mod}`}>
                        {job.status === 'processing' ? (
                          <>
                            <FaSpinner className="pe-spin pe-badge-spin" aria-hidden />{' '}
                            {formatExportJobStatusLabel(job.status)}
                          </>
                        ) : (
                          formatExportJobStatusLabel(job.status)
                        )}
                      </span>
                    </td>
                    <td>
                      {subStillRunning && jobSubTotal != null ? (
                        <>
                          <strong>{Number(productCell || 0).toLocaleString()}</strong>{' '}
                          <span className="pe-job-status-hint">/ {jobSubTotal.toLocaleString()}</span>
                        </>
                      ) : (
                        Number(productCell || 0).toLocaleString()
                      )}
                    </td>
                    <td>{formatExportJobDateTime(job.startedAt)}</td>
                    <td>
                      {subStillRunning ? '—' : formatExportDurationSec(job.startedAt, job.updatedAt)}
                    </td>
                    <td className="pe-export-job-actions-col">
                      <div className="pe-export-job-actions-wrap">
                        <button
                          type="button"
                          className="pe-export-job-view-snapshot pe-export-job-icon-action"
                          onClick={() => setBulkJobFilterModalJob(job)}
                          title="Filters and subscriber list used for this run"
                          aria-label="View subscribe job details"
                        >
                          <FaEye aria-hidden />
                        </button>
                        {canRemoveRunningJob ? (
                          <button
                            type="button"
                            className="pe-export-job-cancel"
                            disabled={removingJobId === job.exportJobId}
                            onClick={() => cancelActiveExportJob(job.exportJobId)}
                            title="Cancel this subscribe job"
                          >
                            {removingJobId === job.exportJobId ? (
                              <FaSpinner className="pe-spin" aria-hidden />
                            ) : (
                              <FaBan aria-hidden />
                            )}
                            <span className="pe-export-job-cancel-label">Cancel</span>
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {subscribeJobsHasMore ? (
                <tr className="pe-export-jobs-load-more-row">
                  <td colSpan={8}>
                    <button
                      type="button"
                      className="pe-btn-secondary pe-export-jobs-load-more"
                      onClick={loadMoreSubscribeJobs}
                      disabled={subscribeJobsLoadingMore}
                    >
                      {subscribeJobsLoadingMore ? (
                        <>
                          <FaSpinner className="pe-spin" aria-hidden /> Loading…
                        </>
                      ) : (
                        'Show more'
                      )}
                    </button>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      {subscribeJobs.length === 0 && !bulkSubscribeBusy && (
        <p className="pe-export-history-empty">No subscription runs yet.</p>
      )}
    </div>
  );
}

export function BulkSubscribeJobFilterModal({
  job,
  filterLines,
  formatExportJobDateTime,
  onClose,
}) {
  if (!job) return null;
  return (
    <div
      className="pe-export-fields-overlay pe-job-filter-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="pe-export-fields-sheet pe-job-filter-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pe-subscribe-job-filter-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pe-export-fields-header">
          <h3 id="pe-subscribe-job-filter-title">Bulk subscribe snapshot</h3>
          <button type="button" className="pe-export-fields-close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <p className="pe-job-filter-intro">
          Job <strong>{job.exportJobId}</strong>
          {' · '}
          {formatExportJobDateTime(job.startedAt)}
          {' · '}
          {formatExportJobStatusLabel(job.status)}
          {typeof job.subscriberUserCount === 'number' ? (
            <>
              {' · '}
              <strong>{job.subscriberUserCount}</strong> seller(s)
            </>
          ) : null}
        </p>
        <div className="pe-export-fields-body pe-job-filter-body">
          <h4 className="pe-job-filter-section-title">Filters at run time</h4>
          {filterLines.length === 0 ? (
            <p className="pe-job-filter-none">
              No extra filters were applied — matched the full eligible product catalog for this workflow.
            </p>
          ) : (
            <div className="pe-job-filter-kv-list" role="list">
              {filterLines.map((row, i) => (
                <div className="pe-job-filter-kv-row" key={`${row.label}-${i}`} role="listitem">
                  <div className="pe-job-filter-k">{row.label}</div>
                  <div className="pe-job-filter-v">{row.value}</div>
                </div>
              ))}
            </div>
          )}
          <h4 className="pe-job-filter-section-title">Target users</h4>
          {Array.isArray(job.subscriberUsers) && job.subscriberUsers.length ? (
            <div className="pe-job-filter-chip-wrap">
              {job.subscriberUsers.map((u) => (
                <span className="pe-job-filter-chip" key={String(u._id)}>
                  {[u.displayLabel, u.uniqueId ? `(${u.uniqueId})` : ''].filter(Boolean).join(' ')}
                </span>
              ))}
            </div>
          ) : (
            <p className="pe-job-filter-none">No subscriber snapshots stored for this row.</p>
          )}
        </div>
      </div>
    </div>
  );
}